"use client";

import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Upload, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/axiosClient";
import { Badge } from "@/shared/ui/data-display/badge";
import { Button } from "@/shared/ui/buttons/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import { Alert, AlertDescription } from "@/shared/ui/feedback/alert";
import { Progress } from "@/shared/ui/progress";
import { useSuppressToolWheel } from "@/shared/lib/use-tool-wheel-store";

/**
 * KAN-351: подтверждение победы загрузкой .rofl-реплея.
 *
 * Основной сценарий эпика KAN-347: сыграли — капитан нажал, выбрал файл, матч закрылся.
 * До этого загрузка была доступна ТОЛЬКО организатору, а панель статистики вместо
 * кнопки просила «попросите капитанов прислать .rofl».
 *
 * Про прогресс: он здесь обязателен, а не для красоты. Замеры показали, что серверная
 * обработка занимает десятки миллисекунд, а всё время съедает сам аплоад — .rofl весит
 * 10–30 МБ, и при 10 Мбит/с исходящего это до ~24 секунд. Абстрактный спиннер на такой
 * срок читается как зависание, и капитан начнёт жать кнопку повторно.
 *
 * KAN-352: тот же компонент встраивается в модалку результата у организатора
 * (variant="inline"). Логика аплоада, прогресса и разбора ошибок общая — дублировать
 * её ради другой рамки значило бы развести поведение двух экранов.
 */

interface ReplayConfirmCardProps {
  matchId: string;
  /** Bo1 / Bo3 / Bo5 — от этого зависит подсказка про следующую игру. */
  bestOf?: number;
  /** Вызывается после успешного подтверждения, чтобы страница перечитала матч. */
  onConfirmed?: () => void;
  /** "inline" — без обёртки Card, для встраивания в модалку результата. */
  variant?: "card" | "inline";
  /** Заголовок inline-варианта: у организатора смысл действия другой. */
  title?: string;
}

interface ConfirmSuccess {
  status?: string;
  totalPlayers?: number;
  autoMatched?: number;
  /** Текст ошибки разбора — есть, когда status === "Failed". */
  parseError?: string;
}

/** Ответ бэкенда об ошибке: текст объясняет, ЧТО именно не сошлось. */
interface UploadErrorBody {
  error?: string;
  matchedPlayers?: number;
  requiredPlayers?: number;
  conflict?: boolean;
  disputeId?: string | null;
}

export function ReplayConfirmCard({
  matchId,
  bestOf = 1,
  onConfirmed,
  variant = "card",
  title,
}: ReplayConfirmCardProps) {
  // KAN-867: во время пика / подтверждения результата колесо тулзов не открывается.
  useSuppressToolWheel();
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ConfirmSuccess | null>(null);

  const isUploading = progress !== null;

  const handlePick = () => {
    setError(null);
    inputRef.current?.click();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // чтобы повторный выбор того же файла тоже сработал
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".rofl")) {
      setError(tUi("litReplayConfirmCard.nuzhenFaylRepleyaS"));
      return;
    }

    setError(null);
    setDone(null);
    setProgress(0);

    const form = new FormData();
    form.append("file", file);

    try {
      const res = await api.post<ConfirmSuccess>(
        `/brackets/matches/${encodeURIComponent(matchId)}/replays?intent=ConfirmResult`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            setProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        },
      );

      setDone(res.data ?? {});

      // Разбор мог не удаться: результат при этом засчитан (победитель читается из
      // файла), но статистики по игре не будет. Молчать нельзя — иначе «статистики
      // нет» и «файл не разобрался» для капитана неразличимы, и он идёт к организатору
      // с вопросом, на который есть точный ответ.
      if (res.data?.status === "Failed") {
        toast.warning(
          res.data.parseError
            ? tUi("litReplayConfirmCard.rezultatZaschitanOshibkaRazbora", { error: res.data.parseError })
            : tUi("litReplayConfirmCard.rezultatZaschitanNoRepley"),
        );
      } else {
        toast.success(tToast("replayConfirmCard.rezultatPodtverzhdenPoRepleyu"));
      }

      onConfirmed?.();
    } catch (err) {
      // Текст ошибки формирует бэкенд и он конкретный: состав не совпал, файл уже
      // использован, матч не в эфире. Показываем его как есть — общее «что-то пошло
      // не так» отправило бы капитана к организатору и обесценило автоматизацию.
      const errorBody = (err as { response?: { data?: UploadErrorBody } })
        ?.response?.data;
      setError(
        errorBody?.error ?? tUi("litReplayConfirmCard.neUdalosZagruzitRepley"),
      );
    } finally {
      setProgress(null);
    }
  };

  const body = (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept=".rofl"
        className="hidden"
        aria-label={tUi("replayConfirmCard.faylRepleyaRofl")}
        onChange={handleFile}
        data-testid="replay-file-input"
      />

      {!isUploading && (
        <Button
          onClick={handlePick}
          disabled={isUploading}
          size={variant === "inline" ? "sm" : "default"}
          variant={variant === "inline" ? "outline" : "default"}
        >
          <Upload className="mr-2 h-4 w-4" />
          {tUi("replayConfirmCard.vybratRepley")}
        </Button>
      )}

      {isUploading && (
        <div className="space-y-1.5">
          <Progress value={progress ?? 0} />
          <p className="text-xs text-muted-foreground">
            Загрузка… {progress}%. Файл весит десятки мегабайт, это может занять
            до полуминуты — не закрывайте страницу.
          </p>
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {done && (
        <Alert className="border-primary/30 bg-primary/5">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>
            Реплей принят.{" "}
            {bestOf > 1
              ? tUi("litReplayConfirmCard.esliSeriyaEscheNe")
              : tUi("litReplayConfirmCard.rezultatMatchaZafiksirovan")}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );

  if (variant === "inline") {
    return (
      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title ?? tUi("litReplayConfirmCard.zakrytMatchRepleem")}
        </div>
        {body}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle>{tUi("replayConfirmCard.podtverditPobeduRepleem")}</CardTitle>
          <Badge variant="secondary" className="text-[10px]">
            {tUi("replayConfirmCard.vRazrabotke")}
          </Badge>
        </div>
        <CardDescription>
          {tUi("replayConfirmCard.zagruzite")} <code className="text-xs">.rofl</code> последней игры — платформа
          сама определит победителя и продвинет сетку. Файл лежит в{" "}
          <code className="text-xs">{tUi("replayConfirmCard.dokumentyLeagueOfLegends")}</code>.
        </CardDescription>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
